//  Angular imports
import { ActivatedRoute, Params } from '@angular/router';
import { Component, DestroyRef, Injector, OnInit, afterNextRender, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
//  Application imports
import { ApplicationRoute } from 'app/models/application-route';
import { DeploymentService } from 'app/services/deployment.service';
import { RAccountDetails } from 'app/records/account-details.record';
import { RDeployment } from 'app/records/deployment.record';
import { RActivatedStackInformation } from 'app/records/activated-stack-information.record';
import { UserService } from 'app/services/user.service';
//  Third party imports
import { finalize } from 'rxjs/operators';
import { Status } from 'app/records/status.record';
import { ZFModalComponent } from 'app/modal-module/zf-modal.component';
import { RAlternateCertificate } from 'app/records/alternate-certificate.record';
import { LegendPosition, LineChartModule, PieChartModule } from '@swimlane/ngx-charts';
import { LineChartSeries, LineChartSlice, PieChartSlice, ReferenceLine } from 'app/models/pie-chart-slice';
import { DiskCalculator } from 'app/pipes/disk-calculator';
import { MetricsService } from 'app/services/metrics.service';
import { RMetricData } from 'app/records/metric-data.record';

import * as forge from 'node-forge';
import { VersionService } from 'app/services/version.service';
import { RVersion } from 'app/records/version.record';
import { environment } from 'environments/environment';
import { UpgradeService } from 'app/services/upgrade.service';
import { RUpgradeModel } from 'app/models/upgrade-model';
import { RActivation } from 'app/records/activation.record';
import { ExternalStorageService } from 'app/services/external.storage.service';
import { RExternalStorageDetailModel } from 'app/models/external-storage-detail-model';
import { RStorageData, RStorageDataRow } from 'app/records/storage-data.record';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { NgIf, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, NgFor, JsonPipe, DecimalPipe, DatePipe } from '@angular/common';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { AuditInfoComponent } from '../audit-information/audit-info.component';
import { VerticalBarWidgetComponent } from '../widgets/vertical-bar-widget.component';
import { FormsModule } from '@angular/forms';
import { ScheduledUpgradeComponent } from '../schedule-upgrade/schedule-upgrade.component';
import { CsvDisplayPipe } from '../../pipes/csv-display.pipe';
import { UrlDisplayPipe } from '../../pipes/url-display.pipe';
import { DeploymentTypePipe } from '../../pipes/deployment-type.pipe';
import { PascalToHumanPipe } from '../../pipes/pascal-to-human.pipe';
import { SKUDisplayPipe } from '../../pipes/sku-display.pipe';
import { ErrorDisplayTextPipe } from '../../pipes/error-display.pipe';
import { DiskSizePipe } from '../../pipes/disk-size.pipe';
import { UpgradeDatePipe } from '../../pipes/upgrade-date-pipe';
import { OrgIdPipe } from '../../pipes/orgId.pipe';

/* eslint-disable  @typescript-eslint/no-explicit-any */

@Component({
    selector: 'zf-deployment-details',
    templateUrl: './deployment-details.component.html',
    styleUrls: ['./deployment-details.component.scss'],
    imports: [SidebarComponent, NgIf, LoadingIndicatorComponent, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, LineChartModule, AuditInfoComponent, PieChartModule, VerticalBarWidgetComponent, NgFor, ZFModalComponent, FormsModule, ScheduledUpgradeComponent, JsonPipe, DecimalPipe, DatePipe, CsvDisplayPipe, UrlDisplayPipe, DeploymentTypePipe, PascalToHumanPipe, SKUDisplayPipe, ErrorDisplayTextPipe, DiskSizePipe, UpgradeDatePipe, OrgIdPipe]
})
export class DeploymentDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private userService = inject(UserService);
    private deploymentService = inject(DeploymentService);
    private metricsService = inject(MetricsService);
    private versionService = inject(VersionService);
    private upgradeService = inject(UpgradeService);
    private externalStorageService = inject(ExternalStorageService);
    private destroyRef = inject(DestroyRef);
    private injector = inject(Injector);

    public get isProduction(): boolean {
        return environment.production;
    }
    public readonly ApplicationRoute: typeof ApplicationRoute = ApplicationRoute;
    public readonly LegendPosition: typeof LegendPosition = LegendPosition;
    public readonly serialNumber = signal<string>(undefined);
    public readonly deployment = signal<RDeployment>(undefined);

    public readonly refLines = signal<Array<ReferenceLine>>([]);
    public readonly hasUpdate = signal<boolean>(false);
    public readonly loadingUpdate = signal<boolean>(true);
    public readonly hasScheduledUpdate = signal<boolean>(false);
    public readonly versionUpdate = signal<RVersion>(null);

    // the following are all editable fields which the user can change after
    // deployments
    public readonly editing = signal<boolean>(false);
    public readonly activatedStackInfo = signal<RActivatedStackInformation>(new RActivatedStackInformation());
    public readonly ipV4 = signal<string>('');
    public readonly ipV6 = signal<string>('');
    public readonly additionalContacts = signal<string>('');
    public readonly certificateBody = signal<string>('');
    public readonly certificatePrivate = signal<string>('');
    public readonly certificateChain = signal<string>('');
    public readonly alternateDomain = signal<string>('');
    public readonly certificatePassword = signal<string>('');
    public readonly certificate = signal<forge.pki.Certificate>(null);
    public readonly certificateFilePath = signal<string>('');
    public readonly extractionError = signal<string>('');
    public readonly externalStorageDest = signal<string>('');
    public readonly externalStorageBucket = signal<string>('');
    public readonly externalStorageDirectory = signal<string>('');
    public readonly externalStoragePolicy = signal<object>(undefined);
    public readonly externalStorageLoading = signal<boolean>(true);
    public readonly isRandomUrl = signal<boolean>(false);

    // Used to immediately disable Update button once clicked
    public readonly updating = signal<boolean>(false);
    public readonly displayRegion = signal<string>(undefined);
    public readonly eventsTime = signal<string>('day');
    public readonly storageInfoView = signal<boolean>(false);
    public readonly onlineStorageView = signal<string>('chart');
    public readonly archiveStorageView = signal<string>('chart');
    public readonly onlineStorageInfo = signal<Array<PieChartSlice>>([]);
    public readonly archiveStorageInfo = signal<Array<PieChartSlice>>([]);
    public readonly eventsPerSecInfo = signal<Array<LineChartSeries>>([]);

    public readonly onlineStorageMetrics = signal<Array<LineChartSeries>>(undefined);
    public readonly archiveStorageMetrics = signal<Array<LineChartSeries>>(undefined);

    public readonly extStorages = signal<Array<RExternalStorageDetailModel>>([]);
    public readonly selectedSto = signal<RExternalStorageDetailModel>(undefined);

    public readonly editState = signal<string>('network');
    public readonly certificateState = signal<string>('file');

    public readonly scheduling = signal<boolean>(false);
    public readonly minUpdateDate: Date = new Date();
    public readonly maxUpdateDate: Date = new Date();
    public readonly scheduledDate = signal<string>(undefined);
    public readonly upgrade = signal<RUpgradeModel>(new RUpgradeModel());
    public readonly newStorage = signal<boolean>(true);
    public readonly storageDetail = signal<RExternalStorageDetailModel>(new RExternalStorageDetailModel());
    public readonly deletingStorage = signal<boolean>(false);

    public readonly changingStorages = signal<boolean>(false);

    public get account(): RAccountDetails {
        return this.userService.accountSelected()?.details;
    }

    private readonly editModal = viewChild<ZFModalComponent>('editModal');
    private readonly scheduleModal = viewChild<ZFModalComponent>('scheduleModal');
    private readonly storageModal = viewChild<ZFModalComponent>('storageModal');

    private tmpCertificate: File = null;

    public ngOnInit(): void {
        this.route.params
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                (params: Params): void => {
                    this.serialNumber.set(params.serial);
                    this.getAccount();
                }
            );

        // minimum date is today + 1 day (tomorrow)
        this.minUpdateDate.setDate(this.minUpdateDate.getDate() + 1);
        // max 2 months from today
        this.maxUpdateDate.setDate(this.minUpdateDate.getDate() + 60);
    }

    public isUpdatePossible(deployment: RDeployment): boolean {
        return deployment.information.status === Status.LICENSE_IN_PROGRESS ||
            deployment.information.status === Status.CREATE_FAILED ||
            deployment.information.status === Status.UPDATE_COMPLETED ||
            deployment.information.status === Status.UPDATE_FAILED ||
            deployment.information.status === Status.COMPLETE;
    }

    public edit(): void {
        const deployment: RDeployment = this.deployment();
        this.editing.set(true);
        // clear any residual values if we ran edit previously
        this.ipV4.set(deployment.information.ipV4Cidr.fromCsv() ?? '');
        this.ipV6.set(deployment.information.ipV6Cidr.fromCsv() ?? '');
        this.additionalContacts.set(deployment?.information?.additionalContacts?.fromCsv() ?? '');
        this.externalStorageDest.set(deployment?.information?.externalStorageDest);
        const [bucket, ...rest] = this.externalStorageDest().split('/');
        this.externalStorageBucket.set(bucket);
        this.externalStorageDirectory.set(rest.length > 0 ? rest.join('/') : '');
        this.isRandomUrl.set(deployment.information.isRandomUrl);

        this.generatePolicy();

        this.certificateBody.set('');
        this.certificatePrivate.set('');
        this.certificateChain.set('');
        this.extractionError.set('');
        this.certificatePassword.set('');
        this.certificate.set(null);
        this.certificateFilePath.set('');
        this.activatedStackInfo.set(new RActivatedStackInformation());

        this.openModal(this.editModal);
    }

    public schedule(): void {
        this.scheduling.set(true);
        this.openModal(this.scheduleModal);
    }

    public selectStorage(storage: RExternalStorageDetailModel) {
        if (this.selectedSto()?.id === storage?.id) {
            this.selectedSto.set(null);
            return;
        }

        this.selectedSto.set(storage);
    }

    public storage(id?: string, isDelete: boolean = false): void {
        this.changingStorages.set(true);

        if (!id) {
            this.newStorage.set(true);
            const storageDetail: RExternalStorageDetailModel = new RExternalStorageDetailModel();
            storageDetail.serialNumber = this.serialNumber();
            // default to all
            storageDetail.organizationId = -1;
            this.storageDetail.set(storageDetail);
            this.externalStorageBucket.set('');
            this.externalStorageDirectory.set('');
            this.externalStoragePolicy.set(undefined);
        } else {
            this.newStorage.set(false);
            const fromArray = this.extStorages().find(x => x.id === id);
            const storageDetail: RExternalStorageDetailModel = Object.assign(new RExternalStorageDetailModel(), fromArray);
            this.storageDetail.set(storageDetail);
            const [bucket, ...rest] = storageDetail.externalStorageDest.split('/');
            this.externalStorageBucket.set(bucket);
            this.externalStorageDirectory.set(rest.length > 0 ? rest.join('/') : '');
            this.generatePolicy();
        }

        this.deletingStorage.set(isDelete);

        this.openModal(this.storageModal);
    }

    public addToStorage(event: MouseEvent) {
        event.stopPropagation();

        if (!this.storageDetail().validate()) {
            // stop do not proceed
            return;
        }

        this.externalStorageService.addStorage(this.account.accountId, this.storageDetail()).subscribe((act: RActivation) => {
            if (act) {
                this.changingStorages.set(false);
                this.storageModal().toggle();
                this.getExternalStorage(this.account.accountId, this.serialNumber());
            }
            this.storageDetail.set(new RExternalStorageDetailModel());
        });

    }

    public deleteFromStorage(event: MouseEvent) {
        event.stopPropagation();

        this.externalStorageService.deleteStorage(this.account.accountId, this.storageDetail()).subscribe((act: RActivation) => {
            if (act) {
                this.changingStorages.set(false);
                this.storageModal().toggle();
                this.getExternalStorage(this.account.accountId, this.serialNumber());
            }
            this.storageDetail.set(new RExternalStorageDetailModel());
            this.selectedSto.set(null);
        });
    }

    public updateScheduledDate(event: string) {
        if (event) {
            this.scheduledDate.set(event);
        }
    }

    public scheduleUpdate(event: MouseEvent): void {
        event.stopPropagation();

        const upgrade = new RUpgradeModel();
        upgrade.serialNumber = this.deployment().serialNumber;
        upgrade.scheduledLocal = this.scheduledDate();
        upgrade.upgradePath = this.versionUpdate().upgradePath;
        this.upgradeService.addSchedule(this.account.accountId, this.deployment().serialNumber, upgrade).subscribe((act: RActivation) => {
            if (act) {
                this.scheduleModal().toggle();
                this.checkForUpdate(this.account.accountId, this.serialNumber());
            }
        });
    }

    public refresh(): void {
        this.deployment.set(null);
        this.hasUpdate.set(false);
        this.eventsPerSecInfo.set([]);
        this.onlineStorageMetrics.set(null);
        this.archiveStorageMetrics.set(null);
        this.getDeployment(this.account.accountId);
    }

    public editValidate(event: MouseEvent): void {
        event.stopPropagation();

        // re-parse CIDRs into comma-separated items
        // trim the strings
        this.ipV4.update((ipV4: string): string => ipV4 ? ipV4.trim() : '');
        this.ipV6.update((ipV6: string): string => ipV6 ? ipV6.trim() : '');
        this.additionalContacts.update((contacts: string): string => contacts ? contacts.trim() : '');
        this.alternateDomain.update((domain: string): string => domain ? domain.trim() : '');

        const activatedStackInfo: RActivatedStackInformation = this.activatedStackInfo();
        const deployment: RDeployment = this.deployment();
        activatedStackInfo.ipv4CIDRList = this.ipV4().toCsv();
        activatedStackInfo.ipv6CIDRList = this.ipV6().toCsv();
        activatedStackInfo.additionalContacts = this.additionalContacts().toCsv();
        activatedStackInfo.alternateDomain = this.alternateDomain().trim();
        activatedStackInfo.isRandomUrl = this.isRandomUrl();
        let checkDomain: boolean = false;

        if (activatedStackInfo.alternateDomain !== deployment.information.alternateDomain) {
            // we have a difference
            if (activatedStackInfo.alternateDomain && !deployment.information.alternateDomain) {
                // if we have a value in the settings
                // where previously we didn't
                // then we are adding
                checkDomain = true;
            }

            // we must be removing
            // don't recheck
        }

        if (this.certificateBody()) {
            activatedStackInfo.certificate = new RAlternateCertificate();
            activatedStackInfo.certificate.body = this.certificateBody();
            activatedStackInfo.certificate.private = this.certificatePrivate();
            activatedStackInfo.certificate.chain = this.certificateChain();
            checkDomain = true;
        }


        // validating update info looks good
        if (!activatedStackInfo.validate(true, checkDomain)) {
            if (activatedStackInfo.error.get('ipv4') || activatedStackInfo.error.get('ipv6')) {
                this.editState.set('network');
            }

            if (activatedStackInfo.error.get('additionalContacts')) {
                this.editState.set('notifications');
            }

            if (activatedStackInfo.error.get('alternateDomain') ||
                activatedStackInfo.error.get('alternatePublicCertificate') ||
                activatedStackInfo.error.get('alternatePrivateCertificate') ||
                activatedStackInfo.error.get('alternateChainCertificate')) {
                this.editState.set('domain');
            }

            return;
        }

        this.editState.set('confirmation');
    }

    public updateFullBucket(): void {
        let tmpDest = this.externalStorageBucket();
        if (this.externalStorageDirectory().length > 0) {
            const tmpDir = this.externalStorageDirectory().replaceMultipleSlashes();
            tmpDest = `${tmpDest}/${tmpDir}${tmpDir.endsWith('/') ? '' : '/'}`;
        }
        this.storageDetail().externalStorageDest = tmpDest;
        this.generatePolicy();
    }

    public update(deployment: RDeployment, event: MouseEvent): void {
        event.stopPropagation();

        // re-parse CIDRs into comma-separated items
        // trim the strings
        this.ipV4.update((ipV4: string): string => ipV4 ? ipV4.trim() : '');
        this.ipV6.update((ipV6: string): string => ipV6 ? ipV6.trim() : '');
        this.additionalContacts.update((contacts: string): string => contacts ? contacts.trim() : '');
        this.alternateDomain.update((domain: string): string => domain ? domain.trim() : '');

        const activatedStackInfo: RActivatedStackInformation = this.activatedStackInfo();
        activatedStackInfo.ipv4CIDRList = this.ipV4().toCsv();
        activatedStackInfo.ipv6CIDRList = this.ipV6().toCsv();
        activatedStackInfo.additionalContacts = this.additionalContacts().toCsv();
        activatedStackInfo.externalStorageDest = this.externalStorageDest();

        let checkDomain: boolean = false;
        activatedStackInfo.alternateDomain = this.alternateDomain();

        // do we have a value for the alternate
        if (this.alternateDomain() && this.alternateDomain() !== this.deployment().information.alternateDomain) {
            checkDomain = true;
        }

        // validating update info looks good
        if (!activatedStackInfo.validate(true, checkDomain)) { return; }

        this.updating.set(true);

        this.deploymentService.update(this.account.accountId, deployment.serialNumber, activatedStackInfo)
            .pipe(
                finalize(
                    (): void => {
                        this.updating.set(deployment.information.status === Status.UPDATE_IN_PROGRESS);
                        this.editing.set(false);
                        this.editModal().toggle();
                    }
                )
            )
            .subscribe({
                next: (): void => this.getDeployment(this.account.accountId),
                error: (error: HttpErrorResponse): void => {

                    console.error(error.message);
                }
            });
    }

    public toGBytes(bytes: number): number {
        const value = bytes ?? 0;
        const toSize = value;
        const num = Math.round((toSize + Number.EPSILON) * 100) / 100;

        // take the value divide until we get to GB
        // and then return a fixed to 2 decimal number back
        return num / (1024 * 1024 * 1024);
    }

    public copyToClipboard(event: MouseEvent, input: string) {
        event.preventDefault();
        if (!input) {
            return;
        }
        input = input.endsWith('.') ? input.stripLast().stripProtocol() : input.stripProtocol();
        navigator.clipboard.writeText(input);
        alert(`Copied ${input} to clipboard`);
    }

    public copyToClipboardObject(event: MouseEvent, input: object) {
        event.preventDefault();
        if (!input) {
            return;
        }
        const jsonStr: string = JSON.stringify(input, null, 4);
        navigator.clipboard.writeText(jsonStr);
        alert('Copied AWS policy to clipboard');
    }

    public refreshMetrics(): void {
        this.getMetrics(this.account.accountId, this.serialNumber(), this.deployment().allocatedEPS);
    }

    public setCertificate(event: Event): void {
        const eventTarget: HTMLInputElement = event.target as HTMLInputElement;
        const cert: File = eventTarget.files.item(0);

        if (this.extractionError()) {
            this.extractionError.set('');
        }

        if (cert.type !== 'application/x-pkcs12') {
            // the type is not of th expected format
            this.extractionError.set('File type is not PKCS#12 (.p12).');
            return;
        }

        // max file size for pkcs#12 is 40KB
        if (cert.size >= (40 * 1024 * 1024)) {
            this.extractionError.set('PKCS#12 file exceeds maximum allowed.');
            return;
        }

        this.tmpCertificate = cert;
        this.certificateFilePath.set(cert.name);
    }

    public handleCertificate(event: Event): void {

        event.stopPropagation();
        if (this.extractionError()) {
            this.extractionError.set('');
        }

        const cert = this.tmpCertificate;
        const reader: FileReader = new FileReader();
        reader.onload = () => {
            try {
                // read data from file reader into DER format (pkcs12 is der format)
                //
                const p12Asn1 = forge.asn1.fromDer(reader.result as any);

                // create our p12 object, using the provided password
                const p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, this.certificatePassword());

                // this is the main certificate bag (will contain 2 (public cert, ca certs))
                const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });

                // get the private certificate
                const pkeyBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag });
                const allCerts = certBags[forge.pki.oids.certBag];

                // main certificate is here always
                const mainCertificate: forge.pki.Certificate = allCerts[0].cert;
                this.certificate.set(mainCertificate);
                // if we have more than 1 use the first one as the CA certificate
                const chainCertificate: forge.pki.Certificate = allCerts.length > 1 ? allCerts[1].cert : null;

                // fetching keyBag
                const keybag = pkeyBags[forge.pki.oids.pkcs8ShroudedKeyBag][0];
                // generate pem from private key
                const privateKeyPem = forge.pki.privateKeyToPem(keybag.key);
                // generate pem from cert
                const certificatePem: string = forge.pki.certificateToPem(mainCertificate);
                // generate pem from CA chain - it can be empty
                const chainCertPem: string = chainCertificate !== null ? forge.pki.certificateToPem(chainCertificate) : '';
                const certCN = mainCertificate.subject.getField('CN')?.value ?? 'unresolved';

                this.certificateBody.set(certificatePem);
                this.certificatePrivate.set(privateKeyPem);
                this.certificateChain.set(chainCertPem);
                this.alternateDomain.set(certCN);
            } catch (e) {
                // capture any extraction errors
                this.extractionError.set(String(e));
            }
        };

        // trigger the read
        reader.readAsBinaryString(cert);
    }

    public formatDiskSize(value: number): string {
        return DiskCalculator.toHuman(value, 2);
    }

    // The modals sit behind *ngIf on editing / scheduling / changingStorages, so the
    // one being opened only exists once the signal written just before this call has
    // been rendered. afterNextRender runs after that render.
    private openModal(modal: () => ZFModalComponent): void {
        afterNextRender((): void => modal()?.toggle(), { injector: this.injector });
    }

    private generatePolicy(): void {
        const bucket: string = this.externalStorageBucket();
        if (bucket.length === 0) {
            // settings this to undefined means it won't
            // show up as `{}` in the text area
            this.externalStoragePolicy.set(undefined);
            return;
        }

        const externalStorage: string = this.deployment().information.externalStorage;
        this.externalStoragePolicy.set({
            'Version': '2012-10-17',
            'Statement': [
                {
                    'Resource': `arn:aws:s3:::${bucket}/*`,
                    'Effect': 'Allow',
                    'Principal': {
                        'AWS': `${externalStorage}`
                    },
                    'Action': 's3:PutObject'
                },
                {
                    'Resource': `arn:aws:s3:::${bucket}`,
                    'Effect': 'Allow',
                    'Principal': {
                        'AWS': `${externalStorage}`
                    },
                    'Action': 's3:ListBucket'
                }
            ]
        });

    }

    private getAccount(): void {
        if (this.account) {
            this.getDeployment(this.account.accountId);
        }
    }

    private getDeployment(accountId: number): void {
        this.checkForUpdate(accountId, this.serialNumber());

        this.deploymentService.getDeployment(accountId, this.serialNumber()).subscribe(
            (deployment: RDeployment): void => {
                this.deployment.set(deployment);
                this.ipV4.set(deployment.information.ipV4Cidr.split(',').join('\n'));
                this.ipV6.set(deployment.information.ipV6Cidr.split(',').join('\n'));
                this.alternateDomain.set(deployment.information.alternateDomain);
                this.additionalContacts.set(deployment?.information?.additionalContacts?.split(',').join('\n'));
                this.displayRegion.set(deployment.information.displayRegion);
                this.isRandomUrl.set(deployment.information.isRandomUrl);
                this.getExternalStorage(accountId, deployment.serialNumber);
                this.getStorageMetrics(accountId, deployment.serialNumber);
                this.getStorageInfo();
                this.getMetrics(accountId, deployment.serialNumber, deployment.allocatedEPS);
            }
        );
    }

    private checkForUpdate(accountId: number, serialNumber: string): void {
        this.loadingUpdate.set(true);
        this.versionService.checkForUpdate(accountId, serialNumber).subscribe(
            (versions: Array<RVersion>): void => {
                if (versions.length === 0) {
                    this.loadingUpdate.set(false);
                    return;
                }
                // sort version by descending order of new version first
                // [7.1.7, 7.1.8, 7.2.0] => [7.2.0, 7.1.8, 7.1.7]
                versions = versions.sort((a, b) => b.newVersion.localeCompare(a.newVersion, undefined, { numeric: true }));
                this.upgradeService.hasUpgradeScheduled(accountId, serialNumber)
                    .subscribe((upgrades: Array<RUpgradeModel>) => {
                        this.hasUpdate.set(true);
                        this.versionUpdate.set(versions.at(0));
                        this.hasScheduledUpdate.set(false);
                        if (upgrades.length > 0) {
                            const scheduled = upgrades.find((u) => u.upgradePath === versions.at(0).upgradePath);
                            if (scheduled && scheduled.status === 'Pending') {
                                this.hasScheduledUpdate.set(true);
                                this.upgrade.set(scheduled);
                                this.scheduledDate.set(scheduled.scheduledLocal);
                            }
                        }
                        this.loadingUpdate.set(false);
                    });

            });
    }

    private getExternalStorage(accountId: number, serialNumber: string) {
        this.externalStorageLoading.set(true);
        this.extStorages.set([]);
        this.externalStorageService.getExternalStorages(accountId, serialNumber)
            .subscribe((d: Array<RExternalStorageDetailModel>) => {
                this.externalStorageLoading.set(false);
                this.extStorages.set(d);
            });
    }

    private getStorageMetrics(accountId: number, serialNumber: string) {
        this.metricsService.getStorageMetrics(accountId, serialNumber).subscribe((storageData: RStorageDataRow) => {
            this.onlineStorageMetrics.set([]);
            this.archiveStorageMetrics.set([]);

            if (!storageData) {
                return;
            }

            const onlineSeries = storageData.online
                ? this.getBarChartData('Online', storageData.online.unique('day'))
                : null;
            const archiveSeries = storageData.archive
                ? this.getBarChartData('Archive', storageData.archive.unique('day'))
                : null;

            if (onlineSeries && onlineSeries.length > 0) {
                this.onlineStorageMetrics.set(onlineSeries);
            }

            if (archiveSeries && archiveSeries.length > 0) {
                this.archiveStorageMetrics.set(archiveSeries);
            }
        });
    }
    private getBarChartData(seriesLabel: string, items: Array<RStorageData>): Array<LineChartSeries> {
        const data: Array<LineChartSeries> = [];
        for (const metric of items.sort((a, b) => new Date(a.day).valueOf() - new Date(b.day).valueOf())) {
            data.push(new LineChartSeries(metric.day, [
                new LineChartSlice(seriesLabel, metric.bytes)
            ]));
        }
        return data;
    }

    private getMetrics(accountId: number, serialNumber: string, maxAllowed: number): void {
        this.eventsPerSecInfo.set([]);
        this.refLines.set([{
            name: 'Allocated compute',
            value: maxAllowed
        }]);

        this.metricsService.getMetrics(accountId, serialNumber, 'Events', this.eventsTime()).subscribe(
            (d: Array<RMetricData>) => {
                let dropFirst = true;
                const lineChartData: Array<LineChartSlice> = [];
                for (const metric of d) {
                    if (dropFirst) {
                        dropFirst = false;
                        continue;
                    }
                    const data = new LineChartSlice(new Date(metric.key), metric.value);
                    lineChartData.push(data);
                }
                const series = new LineChartSeries('Events per second', lineChartData);
                this.eventsPerSecInfo.update((info: Array<LineChartSeries>): Array<LineChartSeries> => [...info, series]);
            }
        );
    }

    private getStorageInfo(): void {
        const deployment: RDeployment = this.deployment();
        if (!deployment) {
            return;
        }

        const onlineStorageCap = this.fromQuantityToBytes(deployment.entitlement.onlineStorage?.quantity ?? 0);
        const archiveStorageCap = this.fromQuantityToBytes(deployment.entitlement.archiveStorage?.quantity ?? 0);
        let online = deployment.information.onlineSizeUsage ?? 0;
        let archive = deployment.information.archiveSizeUsage ?? 0;

        if (online > onlineStorageCap) {
            online = onlineStorageCap - 0.1;
        }

        if (archive > archiveStorageCap) {
            archive = archiveStorageCap - 0.1;
        }

        const onlineUsed = online > 0 ? online : 0;
        const onlineFree = online > 0 ? onlineStorageCap - online : onlineStorageCap;
        const archiveUsed = archive > 0 ? archive : 0;
        const archiveFree = archive > 0 ? archiveStorageCap - archive : archiveStorageCap;
        this.onlineStorageInfo.set([
            {
                name: `used (${DiskCalculator.toHuman(onlineUsed, 2)})`, value: onlineUsed
            },
            {
                name: `free (${DiskCalculator.toHuman(onlineFree, 2)})`, value: onlineFree
            },
        ]);

        this.archiveStorageInfo.set([
            {
                name: `used (${DiskCalculator.toHuman(archiveUsed, 2)})`, value: archiveUsed
            },
            {
                name: `free (${DiskCalculator.toHuman(archiveFree, 2)})`, value: archiveFree
            },
        ]);

    }

    private fromQuantityToBytes(quantity: number): number {
        const value = quantity ?? 0;
        const bytes = (value * (500 * 1000 * 1000 * 1000));
        return bytes;
    }
}
